import { useState, useEffect, useCallback } from "react";
import { SearchConfig } from "../types";
import { useAuth } from "@/features/authentication";
import { useLocation } from "react-router-dom";
import { getDefaultPropertyType } from "../constants/property-types";

const STORAGE_KEY = "vision360_search_config";
const DEFAULT_DISTANCE = 30;

export function useSearchConfig() {
  const [config, setConfig] = useState<SearchConfig | null>(null);
  const { userInfo } = useAuth();
  const location = useLocation();

  const userId = userInfo?.user?.id;
  const fullName = `${userInfo?.user?.first_name} ${userInfo?.user?.last_name}`;
  const userEmail = userInfo?.profile?.email;

  useEffect(() => {
    if (userInfo) {
      saveConfig({
        userId: userId?.toString() ?? "",
        userName: fullName,
        userEmail: userEmail ?? "",
      });
    }
  }, [userInfo]);

  useEffect(() => {
    const loadConfig = () => {
      const longListId = new URLSearchParams(window.location.search).get(
        "l_id",
      );
      const shortListId = new URLSearchParams(window.location.search).get(
        "s_id",
      );
      const savedConfig = localStorage.getItem(STORAGE_KEY);

      if (savedConfig) {
        try {
          const parsedConfig = JSON.parse(savedConfig);

          if (longListId && parsedConfig.longListId !== longListId) {
            const updatedConfig = {
              ...parsedConfig,
              longListId: longListId,
              updatedAt: new Date().toISOString(),
            };
            setConfig((prev) => {
              return {
                ...prev,
                ...updatedConfig,
              };
            });
            localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedConfig));
            return;
          }

          if (shortListId && parsedConfig.shortListId !== shortListId) {
            const updatedConfig = {
              ...parsedConfig,
              shortListId: shortListId,
              updatedAt: new Date().toISOString(),
            };
            setConfig((prev) => {
              return {
                ...prev,
                ...updatedConfig,
              };
            });
            localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedConfig));
            return;
          }

          setConfig((prev) => {
            return {
              ...prev,
              ...parsedConfig,
            };
          });
        } catch (error) {
          console.error("Error parsing saved config:", error);
          localStorage.removeItem(STORAGE_KEY);
          createNewConfig(longListId, shortListId);
        }
      } else {
        createNewConfig(longListId, shortListId);
      }
    };

    const createNewConfig = (
      longListId?: string | null,
      shortListId?: string | null,
    ) => {
      const newConfig: SearchConfig = {
        longListId: longListId || null,
        shortListId: shortListId || null,
        dealId: undefined,
        dealName: undefined,
        userId: userId?.toString() ?? "",
        userName: fullName,
        userEmail: userEmail ?? "",
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        currentStep: "setup",
        filters: {
          query: "",
          propertyType: getDefaultPropertyType(),
          isFurnished: false,
          budgetMin: "",
          budgetMax: "",
          sizeMin: "",
          sizeMax: "",
          positionsMin: "",
          positionsMax: "",
          minPrice: undefined,
          maxPrice: undefined,
          minCapacity: undefined,
          maxCapacity: undefined,
          privateOfficeStatus: ["0"],
          amenitiesSlugs: [],
          amenitiesSlugsOptional: [],
          distance: DEFAULT_DISTANCE,
          unitCreditPrice: 80,
          capacityWeight: undefined,
          priceWeight: undefined,
          searchType: undefined,
          location: undefined,
          latitude: undefined,
          longitude: undefined,
          spaceId: undefined,
          privateOfficeIds: undefined,
          cityName: undefined,
          neighborhood: undefined,
        },
        selectedCards: [],
        longListSelectedCards: [],
        feedback: {},
      };
      setConfig((prev) => {
        return {
          ...prev,
          ...newConfig,
        };
      });
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newConfig));
    };

    loadConfig();
  }, [location.pathname, location.search, userId, fullName, userEmail]);

  const saveConfig = useCallback((updates: Partial<SearchConfig>) => {
    setConfig((prev) => {
      if (!prev) return null;
      const newConfig = {
        ...prev,
        ...updates,
        updatedAt: new Date().toISOString(),
      };

      localStorage.setItem(STORAGE_KEY, JSON.stringify(newConfig));

      return newConfig;
    });
  }, []);

  const clearConfig = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setConfig(null);
  }, []);

  return { config, saveConfig, clearConfig };
}
